import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { auth, firestore } from '../../../api/config';
import { SVG_IMAGES } from "../../../assets";
import { BUNDLE_APP_NAME } from "../../../../app_constant";

const LicenseCodeInput = ({ onActivationSuccess, theme }) => {
  const [licenseCode, setLicenseCode] = useState('');
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showEmailMismatchConfirmation, setShowEmailMismatchConfirmation] = useState(false);
  const [pendingLicenseData, setPendingLicenseData] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);

  const handleActivateLicense = async () => {
    if (!licenseCode.trim()) {
      setError('Please enter a license code');
      return;
    }
    
    // Reset error and set loading state
    setError('');
    setActivating(true);
    
    try {
      // Query Firestore to find the license
      const licensesRef = firestore()
        .collection('bulkpurchases/licenses/items');
      
      const licenseQuery = await licensesRef
        .where('license_code', '==', licenseCode.trim())
        .get();
      
      if (licenseQuery.empty) {
        setError('Invalid license code. Please check and try again.');
        setActivating(false);
        return;
      }
      
      const licenseDoc = licenseQuery.docs[0];
      const licenseData = {
        id: licenseDoc.id,
        ...licenseDoc.data()
      };
      
      // Check if the license is already activated
      if (licenseData.status === 'activated') {
        setError('This license has already been activated.');
        setActivating(false);
        return;
      }
      
      // Check if the license is assigned
      if (licenseData.status !== 'assigned') {
        setError('This license code is not yet ready for activation.');
        setActivating(false);
        return;
      }
      
      // Check if the license was issued for this app
      if (licenseData.application_id !== BUNDLE_APP_NAME) {
        setError('This license is not valid for this application.');
        setActivating(false);
        return;
      }

      // Check if the license is assigned to a different email
      const currentUserEmail = auth().currentUser?.email;
      if (licenseData.assigned_to && licenseData.assigned_to !== currentUserEmail) {
        // Store the license data for later if confirmation is given
        setPendingLicenseData(licenseData);
        // Show confirmation dialog
        setShowEmailMismatchConfirmation(true);
        setActivating(false);
        return;
      }
      
      // If we got here, there's no email mismatch, proceed with activation
      await completeLicenseActivation(licenseData);
      
    } catch (error) {
      console.error('Error activating license:', error);
      setError('Failed to activate license. Please try again or contact support.');
      setActivating(false);
    }
  };

  const completeLicenseActivation = async (licenseData) => {
    setActivating(true);
    
    try {
      const currentUserEmail = auth().currentUser?.email;
      const licensesRef = firestore().collection('bulkpurchases/licenses/items');

      const expirationDate = new Date();
      expirationDate.setMonth(expirationDate.getMonth() + licenseData.period);
      
      // Update the license to activated status
      await licensesRef.doc(licenseData.id).update({
        status: 'activated',
        activated_by: auth().currentUser?.email,
        activated_at: firestore.FieldValue.serverTimestamp(),
        activation_email: currentUserEmail,
        activation_email_different: licenseData.assigned_to !== currentUserEmail
      });
      
      // Update user subscription data
      const userId = auth().currentUser?.uid;
      const appId = licenseData.application_id || 'DEFAULT_APP_NAME';
      
      const subscriptionData = {
        paymentMode: 'BULK_PURCHASE',
        licenseCode: licenseCode,
        subscription_expiration: expirationDate.getTime(),
        assigned_email: licenseData.assigned_to,
        period: licenseData.period
      };
      
      await firestore()
        .doc(`/apps/${appId}/users/${userId}`)
        .set({
          subscription: subscriptionData
        }, { merge: true });
      
      // Store subscription details for the success screen
      setSubscriptionDetails({
        ...subscriptionData,
        expirationDate,
        applicationName: licenseData.application_name
      });
      
      // Show success state
      setSuccess(true);
      setPendingLicenseData(null);
      setShowEmailMismatchConfirmation(false);
      
      // Call success callback after a short delay
      setTimeout(() => {
        onActivationSuccess(subscriptionData);
      }, 2000);
      
    } catch (error) {
      console.error('Error completing license activation:', error);
      setError('Failed to activate license. Please try again or contact support.');
    } finally {
      setActivating(false);
    }
  };

  const handleContinue = () => {
    if (subscriptionDetails) {
      onActivationSuccess(subscriptionDetails);
    }
  };

  const resetForm = () => {
    setLicenseCode('');
    setError('');
    setSuccess(false);
    setSubscriptionDetails(null);
  };

  const styles = {
    container: {
      background: theme?.background?.blue,
      borderRadius: '1rem',
      padding: '2rem',
      border: `1px solid ${theme?.primary?.blue?.[500]}40`,
      marginBottom: '2rem',
      width: '100%',
    },
    content: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '2rem',
      flexWrap: 'wrap',
    },
    textSection: {
      flex: 1,
      minWidth: '300px',
      display: 'flex',
      alignItems: 'center',
      gap: '1.5rem',
    },
    icon: {
      fontSize: '2.5rem',
      color: theme?.primary?.blue?.[500],
      flexShrink: 0,
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      marginBottom: '0.5rem',
    },
    subtitle: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      marginBottom: '1rem',
    },
    inputContainer: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'center',
      marginBottom: '1rem',
      width: '100%',
    },
    input: {
      padding: '0.75rem 1rem',
      border: `1px solid ${theme?.primary?.blue?.[300]}`,
      borderRadius: '0.5rem',
      fontSize: '0.875rem',
      background: theme?.background?.main,
      color: theme?.text?.primary,
      flex: 1,
      minWidth: '300px',
      textTransform: 'uppercase',
    },
    button: {
      padding: '1rem 2rem',
      background: theme?.primary?.blue?.[500],
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexShrink: 0,
    },
    buttonHover: {
      background: theme?.primary?.blue?.[600],
      transform: 'translateY(-2px)',
      boxShadow: theme?.shadows?.lg,
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    error: {
      color: theme?.error?.primary,
      fontSize: '0.875rem',
      marginBottom: '1rem',
      padding: '0.75rem',
      background: theme?.error?.background,
      borderRadius: '0.5rem',
      border: `1px solid ${theme?.error?.border}`,
    },
    success: {
      color: theme?.success?.primary,
      fontSize: '0.875rem',
      marginBottom: '1rem',
      padding: '0.75rem',
      background: theme?.success?.background,
      borderRadius: '0.5rem',
      border: `1px solid ${theme?.success?.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    successDetails: {
      background: theme?.primary?.blue?.[50],
      borderRadius: '0.5rem',
      padding: '1rem',
      marginBottom: '1rem',
      border: `1px solid ${theme?.primary?.blue?.[200]}`,
    },
    successText: {
      color: theme?.primary?.blue?.[800],
      fontSize: '0.875rem',
      marginBottom: '0.25rem',
    },
    continueButton: {
      padding: '1rem 2rem',
      background: theme?.primary?.green?.[500],
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    continueButtonHover: {
      background: theme?.primary?.green?.[600],
      transform: 'translateY(-2px)',
      boxShadow: theme?.shadows?.lg,
    },
    resetButton: {
      padding: '0.5rem 1rem',
      background: 'transparent',
      color: theme?.text?.secondary,
      border: `1px solid ${theme?.borders?.primary}`,
      borderRadius: '0.5rem',
      fontSize: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '0.5rem',
    },
    resetButtonHover: {
      background: theme?.backgrounds?.cardHover,
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      background: theme?.background?.main,
      borderRadius: '0.75rem',
      padding: '1.5rem',
      maxWidth: '400px',
      width: '90%',
      border: `1px solid ${theme?.borders?.primary}`,
    },
    modalTitle: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      marginBottom: '1rem',
    },
    modalText: {
      color: theme?.text?.secondary,
      marginBottom: '1rem',
    },
    modalButtons: {
      display: 'flex',
      gap: '0.75rem',
      justifyContent: 'flex-end',
    },
    modalButton: {
      padding: '0.5rem 1rem',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    modalButtonSecondary: {
      background: theme?.backgrounds?.card,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary}`,
    },
    modalButtonPrimary: {
      background: theme?.primary?.blue?.[500],
      color: 'white',
      border: 'none',
    },
  };

  // Show success state
  if (success && subscriptionDetails) {
    return (
      <motion.div 
        style={styles.container}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div style={styles.content}>
          <div style={styles.textSection}>
            <FontAwesomeIcon icon={faCheck} style={styles.icon} />
            <div>
              <h3 style={styles.title}>Premium Access Activated!</h3>
              <div style={styles.successDetails}>
                <div style={styles.successText}>
                  <strong>Premium Access:</strong> {subscriptionDetails.period} months
                </div>
                <div style={styles.successText}>
                  <strong>Valid Until:</strong> {subscriptionDetails.expirationDate.toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
          
          <motion.button
            style={styles.continueButton}
            whileHover={styles.continueButtonHover}
            whileTap={{ scale: 0.95 }}
            onClick={handleContinue}
          >
            Continue to Dashboard
          </motion.button>
        </div>
        
        
      </motion.div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <motion.div 
        style={styles.container}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div style={styles.content}>
          <div style={styles.textSection}>
            <FontAwesomeIcon icon={faKey} style={styles.icon} />
            <div>
              <h3 style={styles.title}>Have a License Code?</h3>
              <p style={styles.subtitle}>
                Enter your license code below to activate your premium access
              </p>
              
              <div style={styles.inputContainer}>
                <input
                  type="text"
                  value={licenseCode}
                  onChange={(e) => setLicenseCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX-XXXX"
                  style={styles.input}
                  disabled={activating}
                />
              </div>
              
              {error && (
                <div style={styles.error}>
                  <FontAwesomeIcon icon={faTimes} style={{ marginRight: '0.5rem' }} />
                  {error}
                </div>
              )}
            </div>
          </div>
          
          <motion.button
            style={{
              ...styles.button,
              ...(activating && styles.buttonDisabled)
            }}
            whileHover={!activating ? styles.buttonHover : {}}
            whileTap={!activating ? { scale: 0.95 } : {}}
            onClick={handleActivateLicense}
            disabled={activating}
          >
            {activating ? (
              <>
                <div style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                Activating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faKey} />
                Activate
              </>
            )}
          </motion.button>
        </div>
      </motion.div>

      {/* Email Mismatch Confirmation Modal */}
      {showEmailMismatchConfirmation && pendingLicenseData && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalTitle}>Email Mismatch Detected</div>
            
            <div style={styles.modalText}>
              This license was assigned to a different email address:
            </div>
            
            <div style={{ background: theme?.warning?.background, padding: '0.75rem', borderRadius: '0.375rem', marginBottom: '1rem' }}>
              <div style={{ color: theme?.warning?.text, fontSize: '0.875rem' }}>
                <strong>License assigned to:</strong> {pendingLicenseData.assigned_to}
              </div>
              <div style={{ color: theme?.warning?.text, fontSize: '0.875rem' }}>
                <strong>You are signed in as:</strong> {auth().currentUser?.email}
              </div>
            </div>
            
            <div style={styles.modalText}>
              Are you sure you want to activate this license with your current account?
            </div>
            
            <div style={styles.modalButtons}>
              <button
                style={{ ...styles.modalButton, ...styles.modalButtonSecondary }}
                onClick={() => {
                  setPendingLicenseData(null);
                  setShowEmailMismatchConfirmation(false);
                }}
              >
                Cancel
              </button>
              
              <button
                style={{ ...styles.modalButton, ...styles.modalButtonPrimary }}
                onClick={() => completeLicenseActivation(pendingLicenseData)}
              >
                Yes, Activate License
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LicenseCodeInput;
